import { Divider, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { Link, useNavigate } from "react-router";

import { Button } from "@citrine/atoms/button";
import { SectionHeader } from "@citrine/atoms/header";
import { ForwardIcon } from "@citrine/atoms/icons";
import type { DatasetMetricResponse } from "@citrine/resources/metrics";
import { MetricsClient, hasDatasetData } from "@citrine/resources/metrics";
import type { IProject } from "@citrine/resources/project";
import { ProjectClient } from "@citrine/resources/project";
import { getLookupMap } from "@citrine/utils/helpers";

import { DatasetDashboardCard } from "#src/components/dataset-dashboard-card";
import { QuerySuspense } from "#src/components/query-suspense";
import { useDocumentTitle } from "#src/hooks/use-document-title";
import logoImg from "#src/images/logo.splash.png";
import { DATA_MANAGER_PATH } from "#src/pages/constants";
import { NewProjectDialog } from "#src/pages/projects/components/new-project-dialog";
import { NewProjectCard } from "#src/pages/projects/components/new-project-dialog/new-project-card";
import { ProjectCard } from "#src/pages/projects/components/project-card";

import * as sx from "./route.sx";

export function Component() {
  const metricsQuery = MetricsClient.getInstance().useGetDatasetMetrics();
  const projectsQuery = ProjectClient.getInstance().useFind({
    per_page: 8,
    page: 1,
    sort_by: {
      field: "created_at",
      order: "desc",
    },
    archived: false,
  });

  useDocumentTitle();

  return (
    <QuerySuspense
      queries={[metricsQuery, projectsQuery]}
      render={([metrics, projects]) => {
        return hasDatasetData(metrics) || projects.length ? (
          <StandardContent metrics={metrics} projects={projects} />
        ) : (
          <NewUserContent />
        );
      }}
    />
  );
}

function StandardContent({
  metrics,
  projects,
}: {
  metrics: DatasetMetricResponse;
  projects: IProject[];
}) {
  const navigate = useNavigate();

  const projectDashboardDataQuery =
    MetricsClient.getInstance().useGetProjectsMetrics({ projects });
  const metadataLookup = useMemo(
    () =>
      getLookupMap(
        projectDashboardDataQuery.data ?? [],
        (metadata) => metadata.id,
      ),
    [projectDashboardDataQuery],
  );

  const itemContent = useCallback(
    (_index: number, project: IProject) => (
      <ProjectCard
        key={project.id}
        project={project}
        metadata={metadataLookup?.[project.id]}
      />
    ),
    [metadataLookup],
  );

  return (
    <sx.PageContainer>
      <section>
        <SectionHeader translationId="data-manager.recent-changes">
          <Button
            component={Link}
            to={DATA_MANAGER_PATH}
            variant="text"
            endIcon={<ForwardIcon />}
            size="large"
          >
            <FormattedMessage id="nav.data-manager.action" />
          </Button>
        </SectionHeader>
        <DatasetDashboardCard metrics={metrics} />
      </section>
      <section>
        <SectionHeader translationId="resources.project.recent-changes">
          <Button
            component={Link}
            to="/projects"
            variant="text"
            endIcon={<ForwardIcon />}
            size="large"
          >
            <FormattedMessage id="nav.projects.action" />
          </Button>
        </SectionHeader>

        <sx.CardList
          data={projects}
          itemContent={itemContent}
          emptyContent={
            <NewProjectCard
              onCreate={({ id }) => {
                navigate(`/projects/${id}`);
              }}
            />
          }
        />
      </section>
    </sx.PageContainer>
  );
}

function NewUserContent() {
  const navigate = useNavigate();
  return (
    <sx.HeroContainer>
      {/* Logo section */}
      <sx.HeroItem>
        <sx.Logo src={logoImg} alt="Logo" />
      </sx.HeroItem>

      <Divider orientation="vertical" flexItem />

      {/* Call to action section */}
      <sx.HeroItem>
        <Typography variant="h1">
          <FormattedMessage id="app.welcome" />
        </Typography>

        {/* Data Manager section */}
        <sx.CallsToActionContainer>
          <sx.CallToAction>
            <Typography variant="body1">
              <FormattedMessage id="data-manager.call-to-action" />
            </Typography>
            <Button
              component={Link}
              to={DATA_MANAGER_PATH}
              variant="outlined"
              endIcon={<ForwardIcon />}
              size="large"
            >
              <FormattedMessage id="nav.data-manager" />
            </Button>
          </sx.CallToAction>

          {/* Project section */}
          <sx.CallToAction>
            <Typography variant="body1">
              <FormattedMessage id="resources.project.call-to-action" />
            </Typography>
            <NewProjectDialog
              onCreate={({ id }) => {
                navigate(`/projects/${id}`);
              }}
            />
          </sx.CallToAction>
        </sx.CallsToActionContainer>
      </sx.HeroItem>
    </sx.HeroContainer>
  );
}
